import { Injectable, NgZone, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { ToastService } from '../../@core/services/toast.service';

declare global {
    interface Window {
        webkitSpeechRecognition: any;
        SpeechRecognition: any;
    }
}

@Injectable({
    providedIn: 'root'
})
export class VoiceRecognitionService {
    recognition: any;
    isRecording = signal<boolean>(false);
    isSupported = signal<boolean>(false);
    audioLevel = signal<number>(0);
    public transcript$ = new Subject<string>();

    private audioContext: AudioContext | null = null;
    private analyser: AnalyserNode | null = null;
    private microphone: MediaStreamAudioSourceNode | null = null;
    private mediaStream: MediaStream | null = null;
    private dataArray: Uint8Array | null = null;
    private animationFrameId: number | null = null;
    private isMicrophoneReady = false; // Track if microphone is already initialized
    private currentSessionId: number = 0; // Track recording sessions to prevent old transcripts

    /**
     * Returns true if microphone needs initialization (will require async work)
     * Public method for components to check if spinner should be shown
     */
    needsMicrophoneInit(): boolean {
        return !this.isMicrophoneReady || !this.audioContext || !this.analyser;
    }

    constructor(private ngZone: NgZone, private toastService: ToastService) {
        // Initial check for support
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        this.isSupported.set(!!SpeechRecognition);
    }

    /**
     * Initializes speech recognition if supported by the browser
     * Sets up event handlers for results, errors, and end events
     */
    private initRecognition() {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (SpeechRecognition) {
            this.isSupported.set(true);

            // Clean up existing instance if any
            if (this.recognition) {
                this.recognition.onresult = null;
                this.recognition.onerror = null;
                this.recognition.onend = null;
                try {
                    this.recognition.abort();
                } catch (e) {
                    // Ignore errors during abort
                }
            }

            this.recognition = new SpeechRecognition();
            this.recognition.continuous = true;
            this.recognition.interimResults = false; // Set to true if you want to see typing as you speak
            this.recognition.lang = 'en-US';

            // Capture the current session ID to only process results from this session
            const sessionId = this.currentSessionId;

            this.recognition.onresult = (event: any) => {

                // Ignore results from old sessions
                if (sessionId !== this.currentSessionId) {
                    return;
                }

                let finalTranscript = '';

                for (let i = event.resultIndex; i < event.results.length; ++i) {
                    if (event.results[i].isFinal) {
                        finalTranscript += event.results[i][0].transcript;
                    }
                }

                if (finalTranscript) {
                    this.ngZone.run(() => {
                        this.transcript$.next(finalTranscript);
                    });
                }
            };

            this.recognition.onerror = (event: any) => {
                const errorType = event.error || 'unknown';

                // Stop immediately and ensure recording state is cleared
                this.isRecording.set(false);
                this.cleanupAudioResources();

                // Run toast notification inside Angular zone
                this.ngZone.run(() => {

                    if (errorType === 'no-speech') {
                        this.toastService.info('No speech detected. Please try again.');
                    } else if (errorType === 'audio-capture') {
                        this.toastService.error('No microphone found. Please check your microphone settings.');
                    } else if (errorType === 'not-allowed') {
                        this.toastService.error('Microphone access denied. Please allow microphone permissions in your browser settings.');
                    } else if (errorType === 'network') {
                        this.toastService.error('Speech recognition failed. This may be due to browser limitations. Please check your microphone permissions and try again.');
                    } else if (errorType === 'service-not-allowed') {
                        this.toastService.error('Speech recognition service is not allowed. Please ensure you\'re using HTTPS or localhost.');
                    } else {
                        this.toastService.error(`Speech recognition error: ${errorType}. Please check your microphone permissions.`);
                    }

                });
            };

            this.recognition.onend = () => {
                this.ngZone.run(() => {
                    if (this.isRecording()) {
                        // Unexpected stop (e.g. silence timeout or error)
                        this.cleanupAudioResources();
                        this.isRecording.set(false);
                    }
                });
            };
        } else {
            this.isSupported.set(false);
        }
    }

    /**
     * Starts voice recognition
     * First checks microphone permissions before starting
     */
    async start() {

        // Increment session ID BEFORE initializing recognition
        // This ensures the onresult handler captures the correct session ID
        this.currentSessionId++;

        // Re-initialize recognition to ensure clean state
        // The onresult handler will capture the new session ID
        this.initRecognition();

        if (!this.recognition) {
            return;
        }

        // Check microphone permissions first
        try {
            const permissionStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName });

            if (permissionStatus.state === 'denied') {
                this.isRecording.set(false);
                this.ngZone.run(() => {
                    this.toastService.error('Microphone access is denied. Please enable microphone permissions in your browser settings.');
                });
                throw new Error('Microphone permission denied');
            }
        } catch (error) {
            if (error instanceof Error && error.message === 'Microphone permission denied') {
                throw error; // Re-throw permission error to be caught by component
            }
            // Continue anyway - some browsers don't support permissions API
        }

        try {
            // Start audio analysis first to ensure microphone access
            // This will only initialize if not already ready
            await this.startAudioAnalysis();

            this.recognition.start();

            // Wait a brief moment to ensure recognition has fully started
            // This prevents missing early speech input
            await new Promise(resolve => setTimeout(resolve, 100));

            this.isRecording.set(true);

            // Start the analysis loop now that we are recording
            this.analyze();

        } catch (error) {
            // Clean up if start failed
            this.cleanupAudioResources();
            this.isRecording.set(false);
            this.isMicrophoneReady = false; // Reset ready state on error

            // Only show error toast if it's not a permission error (already shown)
            const isPermissionError = error instanceof Error && error.message === 'Microphone permission denied';
            if (!isPermissionError) {
                this.ngZone.run(() => {
                    this.toastService.error('Failed to start voice recording. Please try again.');
                });
            }

            throw error; // Re-throw so component knows to clear loading state
        }
    }

    stop() {
        if (this.recognition) {
            this.recognition.stop();
            this.isRecording.set(false);
            this.stopAudioAnalysis(); // Stop visualizer
        }
    }

    private async startAudioAnalysis() {
        // If microphone is already ready and stream is active, skip initialization
        if (this.isMicrophoneReady && this.audioContext && this.analyser && this.mediaStream?.active) {
            return;
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            this.mediaStream = stream;
            this.audioContext = new AudioContext();
            this.analyser = this.audioContext.createAnalyser();
            this.microphone = this.audioContext.createMediaStreamSource(stream);
            this.microphone.connect(this.analyser);

            this.analyser.fftSize = 256;
            const bufferLength = this.analyser.frequencyBinCount;
            this.dataArray = new Uint8Array(bufferLength);

            this.isMicrophoneReady = true; // Mark as ready

            // Don't call analyze() here, wait for recognition to start
        } catch (err) {
            this.isMicrophoneReady = false; // Reset on error
            throw err; // Re-throw so caller knows it failed
        }
    }

    private stopAudioAnalysis() {
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }

        // Don't close audio context - keep it ready for next time
        // This prevents needing to re-initialize microphone on every start
        // Only close if we're completely done (e.g., component destroyed)

        this.audioLevel.set(0);
    }

    /**
     * Completely cleanup audio resources (call when component is destroyed)
     */
    cleanupAudioResources() {
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }

        if (this.mediaStream) {
            this.mediaStream.getTracks().forEach(track => track.stop());
            this.mediaStream = null;
        }

        if (this.audioContext) {
            this.audioContext.close();
            this.audioContext = null;
        }

        this.analyser = null;
        this.microphone = null;
        this.dataArray = null;
        this.isMicrophoneReady = false;
        this.audioLevel.set(0);
    }

    private analyze() {
        if (!this.analyser || !this.isRecording()) return;

        this.analyser.getByteFrequencyData(this.dataArray as any);

        // Calculate average volume
        let sum = 0;
        for (let i = 0; i < this.dataArray!.length; i++) {
            sum += this.dataArray![i];
        }
        const average = sum / this.dataArray!.length;

        // Normalize to 0-100 range (boosted for speech visibility)
        const normalizedLevel = Math.min(100, Math.round((average / 255) * 100 * 2.5));

        this.ngZone.run(() => {
            this.audioLevel.set(normalizedLevel);
        });

        this.animationFrameId = requestAnimationFrame(() => this.analyze());
    }

    /**
     * Toggles voice recognition on/off
     */
    toggle() {
        if (this.isRecording()) {
            this.stop();
        } else {
            // Call async start() method
            this.start();
        }
    }
}
